import { AlertCircle, AlertTriangle, CheckCircle2, Info, X } from "lucide-react";
import type { ReactNode } from "react";
import { IconButton } from "../IconButton/IconButton";

export type ToastVariant = "success" | "info" | "warning" | "error";

const ICON_CLASSES: Record<ToastVariant, string> = {
  success: "text-success",
  info: "text-info",
  warning: "text-warning",
  error: "text-error",
};

const ICONS: Record<ToastVariant, ReactNode> = {
  success: <CheckCircle2 size={18} />,
  info: <Info size={18} />,
  warning: <AlertTriangle size={18} />,
  error: <AlertCircle size={18} />,
};

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastProps {
  variant?: ToastVariant;
  title: string;
  description?: string;
  action?: ToastAction;
  onDismiss: () => void;
}

export function Toast({ variant = "info", title, description, action, onDismiss }: Readonly<ToastProps>) {
  const urgent = variant === "warning" || variant === "error";

  return (
    <div
      role={urgent ? "alert" : "status"}
      className="grid w-80 grid-cols-[auto_1fr_auto] items-center gap-x-2.5 gap-y-1 rounded-xl border border-border-default bg-surface-elevated p-3 shadow-lg"
    >
      <span className={ICON_CLASSES[variant]}>{ICONS[variant]}</span>
      <span className="block min-w-0 text-sm font-medium text-content-primary">{title}</span>
      <IconButton variant="ghost" size="sm" aria-label="Dismiss" icon={<X />} onClick={onDismiss} />
      {description && (
        <span className="col-start-2 col-span-2 block text-xs leading-snug text-content-tertiary">{description}</span>
      )}
      {action && (
        <button
          type="button"
          onClick={action.onClick}
          className="col-start-2 col-span-2 mt-1 cursor-pointer justify-self-start text-xs font-semibold text-content-tertiary underline-offset-2 hover:text-content-primary hover:underline"
        >
          {action.label}
        </button>
      )}
    </div>
  );
}
